import { useEffect, useState } from "react";
import api, { INR, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { CheckCircle, Clock, TrendingUp, DollarSign, Download, Pencil, Trash2 } from "lucide-react";
import { exportToExcel, exportToPDF } from "@/lib/exports";

const SUB_DEALERS = ["Alok Automobile", "Dilip Enterprises"];

export default function Ledger() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [subDealer, setSubDealer] = useState(isAdmin ? "" : (user?.branch || ""));
  const [status, setStatus] = useState("");
  const monthStart = () => { const d = new Date(); d.setDate(1); return d.toISOString().slice(0, 10); };
  const todayIso = () => new Date().toISOString().slice(0, 10);
  const [dateFrom, setDateFrom] = useState(monthStart);
  const [dateTo, setDateTo] = useState(todayIso);
  const [data, setData] = useState({ entries: [], summary: { total_incentive: 0, pending: 0, paid: 0, count: 0, avg_per_vehicle: 0 } });
  const [editing, setEditing] = useState(null);

  const fetchAll = async () => {
    const params = {};
    if (subDealer) params.sub_dealer = subDealer;
    if (status) params.status = status;
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;
    const { data: d } = await api.get("/ledgers", { params });
    setData(d);
  };
  useEffect(() => { fetchAll(); /* eslint-disable-line */ }, [subDealer, status, dateFrom, dateTo]);

  const markPaid = async (l) => {
    try { await api.post(`/ledgers/${l.id}/mark-paid`); toast.success("Marked as paid"); fetchAll(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const markPending = async (l) => {
    try { await api.post(`/ledgers/${l.id}/mark-pending`); toast.success("Marked as pending"); fetchAll(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const remove = async (l) => {
    if (!window.confirm(`Are you sure you want to delete this ledger entry (${l.sub_dealer} · ${l.vehicle_model} · ${l.chassis_number})?`)) return;
    try { await api.delete(`/ledgers/${l.id}`); toast.success("Ledger entry deleted"); fetchAll(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const rows = data.entries.map((e, i) => ({
    _sr: i + 1,
    date: new Date(e.delivered_at).toLocaleDateString("en-IN"),
    sub_dealer: e.sub_dealer,
    model: e.vehicle_model,
    rule_label: e.rule_label || "—",
    chassis: e.chassis_number,
    customer: e.customer_name || "—",
    incentive: e.incentive_amount,
    status: e.status,
  }));

  const cols = [
    { key: "_sr", label: "SR. No." },
    { key: "date", label: "Delivered" },
    { key: "sub_dealer", label: "Sub-Dealer" },
    { key: "model", label: "Vehicle Model" },
    { key: "rule_label", label: "Rule" },
    { key: "chassis", label: "Chassis" },
    { key: "customer", label: "Customer" },
    { key: "incentive", label: "Incentive", fmt: (v) => INR(v) },
    { key: "status", label: "Status" },
  ];
  const doExcel = () => exportToExcel("profit-ledger", rows, cols);
  const doPDF = () => exportToPDF(`Profit Ledger${subDealer ? " — " + subDealer : ""}`, rows, cols);

  return (
    <div className="max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-3xl font-bold text-slate-900" data-testid="ledger-heading">
            {isAdmin ? "Sub-Dealer Profit Ledger" : `${user?.branch} — Profit Ledger`}
          </h1>
          <p className="text-sm text-slate-500 mt-1">Auto-posted incentive entries. Tracked per delivery.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={doExcel} data-testid="ledger-export-excel"
            className="inline-flex items-center gap-1 px-3 py-2 rounded-md border border-slate-300 hover:bg-slate-50 text-sm">
            <Download className="w-3.5 h-3.5" /> Excel
          </button>
          <button onClick={doPDF} data-testid="ledger-export-pdf"
            className="inline-flex items-center gap-1 px-3 py-2 rounded-md border border-slate-300 hover:bg-slate-50 text-sm">
            <Download className="w-3.5 h-3.5" /> PDF
          </button>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        <Kpi label="Total Incentive" value={INR(data.summary.total_incentive)} icon={TrendingUp} tone="blue" testid="ledger-kpi-total" />
        <Kpi label="Pending" value={INR(data.summary.pending)} icon={Clock} tone="orange" testid="ledger-kpi-pending" />
        <Kpi label="Paid" value={INR(data.summary.paid)} icon={CheckCircle} tone="green" testid="ledger-kpi-paid" />
        <Kpi label="Vehicles Sold" value={data.summary.count} icon={DollarSign} tone="slate" testid="ledger-kpi-count" />
        <Kpi label="Avg / Vehicle" value={INR(data.summary.avg_per_vehicle || 0)} icon={TrendingUp} tone="blue" testid="ledger-kpi-avg" />
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-4 items-end">
        <label className="text-xs">
          <div className="uppercase tracking-wider text-slate-500 mb-1">From</div>
          <input type="date" value={dateFrom} max={dateTo || todayIso()}
            onChange={(e) => setDateFrom(e.target.value)}
            className="px-3 py-2 border border-slate-300 rounded-md text-sm"
            data-testid="ledger-date-from" />
        </label>
        <label className="text-xs">
          <div className="uppercase tracking-wider text-slate-500 mb-1">To</div>
          <input type="date" value={dateTo} min={dateFrom} max={todayIso()}
            onChange={(e) => setDateTo(e.target.value)}
            className="px-3 py-2 border border-slate-300 rounded-md text-sm"
            data-testid="ledger-date-to" />
        </label>
        <button data-testid="ledger-range-this-month"
          onClick={() => { setDateFrom(monthStart()); setDateTo(todayIso()); }}
          className="text-xs px-3 py-2 rounded-md border border-slate-300 hover:bg-slate-50">
          This Month
        </button>
        <button data-testid="ledger-range-last-month"
          onClick={() => {
            const d = new Date();
            const last = new Date(d.getFullYear(), d.getMonth() - 1, 1);
            const end = new Date(d.getFullYear(), d.getMonth(), 0);
            setDateFrom(last.toISOString().slice(0, 10));
            setDateTo(end.toISOString().slice(0, 10));
          }}
          className="text-xs px-3 py-2 rounded-md border border-slate-300 hover:bg-slate-50">
          Last Month
        </button>
        <button data-testid="ledger-range-clear"
          onClick={() => { setDateFrom(""); setDateTo(""); }}
          className="text-xs px-3 py-2 rounded-md border border-slate-300 hover:bg-slate-50">
          All Time
        </button>
        {isAdmin && (
          <select value={subDealer} onChange={(e) => setSubDealer(e.target.value)}
            className="px-3 py-2 border border-slate-300 rounded-md text-sm bg-white ml-auto" data-testid="ledger-sub-dealer-filter">
            <option value="">All Sub-Dealers</option>
            {SUB_DEALERS.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        )}
        <select value={status} onChange={(e) => setStatus(e.target.value)}
          className="px-3 py-2 border border-slate-300 rounded-md text-sm bg-white" data-testid="ledger-status-filter">
          <option value="">All Statuses</option>
          <option value="pending">Pending</option>
          <option value="paid">Paid</option>
        </select>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg table-scroll">
        <table className="data-table w-full" data-testid="ledger-table">
          <thead>
            <tr>
              <th className="col-sr">SR. No.</th>
              <th>Delivered</th>
              <th>Sub-Dealer</th>
              <th>Vehicle</th>
              <th>Rule</th>
              <th>Customer</th>
              <th className="text-right">Incentive</th>
              <th>Status</th>
              {isAdmin && <th className="text-right pr-6">Actions</th>}
            </tr>
          </thead>
          <tbody>
            {data.entries.length === 0 && (
              <tr><td colSpan={isAdmin ? 9 : 8} className="text-center text-slate-400 py-8">No incentive entries yet.</td></tr>
            )}
            {data.entries.map((l, idx) => (
              <tr key={l.id} data-testid={`ledger-row-${l.id}`}>
                <td className="col-sr">{idx + 1}</td>
                <td className="text-xs text-slate-500">{new Date(l.delivered_at).toLocaleDateString("en-IN")}</td>
                <td>{l.sub_dealer}</td>
                <td>
                  <div className="text-sm font-medium">{l.vehicle_model}</div>
                  <div className="text-xs text-slate-500">{l.chassis_number}</div>
                </td>
                <td>
                  <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-800">
                    {l.rule_label || l.vehicle_model || "—"}
                  </span>
                </td>
                <td className="text-sm">{l.customer_name || "—"}</td>
                <td className="text-right font-semibold text-brand-orange">{INR(l.incentive_amount)}</td>
                <td>
                  <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${l.status === "paid" ? "bg-emerald-100 text-emerald-800" : "bg-orange-100 text-orange-800"}`}>
                    {l.status}
                  </span>
                </td>
                {isAdmin && (
                  <td className="text-right pr-6">
                    <div className="flex justify-end items-center gap-1">
                      {l.status === "pending" ? (
                        <button onClick={() => markPaid(l)} data-testid={`mark-paid-${l.id}`}
                          className="px-3 py-1 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-xs">
                          Mark Paid
                        </button>
                      ) : (
                        <button onClick={() => markPending(l)} data-testid={`mark-pending-${l.id}`}
                          className="px-3 py-1 rounded-md border border-slate-300 hover:bg-slate-50 text-xs">
                          Revert
                        </button>
                      )}
                      <button onClick={() => setEditing(l)} data-testid={`edit-ledger-${l.id}`}
                        className="p-1.5 rounded-md hover:bg-slate-100 text-slate-500" title="Edit">
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                      <button onClick={() => remove(l)} data-testid={`delete-ledger-${l.id}`}
                        className="p-1.5 rounded-md hover:bg-red-50 text-red-500" title="Delete">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <EditLedgerModal entry={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); fetchAll(); }} />
      )}
    </div>
  );
}

function EditLedgerModal({ entry, onClose, onSaved }) {
  const [form, setForm] = useState({
    incentive_amount: entry.incentive_amount ?? 0,
    customer_name: entry.customer_name || "",
    status: entry.status || "pending",
    notes: entry.notes || "",
  });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.patch(`/ledgers/${entry.id}`, {
        incentive_amount: Number(form.incentive_amount) || 0,
        customer_name: form.customer_name,
        status: form.status,
        notes: form.notes,
      });
      toast.success("Ledger entry updated");
      onSaved();
    } catch (e2) { toast.error(formatApiError(e2.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 flex items-center justify-center p-4" data-testid="edit-ledger-modal">
      <form onSubmit={submit} className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
        <div className="font-heading text-xl font-semibold mb-4">Edit Ledger Entry</div>
        <div className="text-xs text-slate-500 mb-4 bg-slate-50 p-2 rounded">
          {entry.sub_dealer} · {entry.vehicle_model} · {entry.chassis_number}
        </div>
        <label className="block mb-3">
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-600 mb-1">Incentive Amount (₹)</div>
          <input type="number" min="0" step="1" value={form.incentive_amount}
            onChange={(e) => setForm({ ...form, incentive_amount: e.target.value })}
            className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm"
            data-testid="edit-ledger-amount" required />
        </label>
        <label className="block mb-3">
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-600 mb-1">Customer Name</div>
          <input value={form.customer_name}
            onChange={(e) => setForm({ ...form, customer_name: e.target.value })}
            className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm"
            data-testid="edit-ledger-customer" />
        </label>
        <label className="block mb-3">
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-600 mb-1">Status</div>
          <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}
            className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm bg-white"
            data-testid="edit-ledger-status">
            <option value="pending">Pending</option>
            <option value="paid">Paid</option>
          </select>
        </label>
        <label className="block mb-3">
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-600 mb-1">Notes</div>
          <input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
            className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm"
            data-testid="edit-ledger-notes" />
        </label>
        <div className="flex justify-end gap-2 mt-4">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-md text-sm border border-slate-300"
            data-testid="edit-ledger-cancel">Cancel</button>
          <button type="submit" disabled={saving} data-testid="edit-ledger-save"
            className="px-4 py-2 rounded-md text-sm bg-brand-blue bg-brand-blue-hover text-white disabled:opacity-60">
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </form>
    </div>
  );
}

const TONES = {
  blue: "bg-blue-50 text-blue-700 border-blue-200",
  orange: "bg-orange-50 text-brand-orange border-orange-200",
  green: "bg-emerald-50 text-emerald-700 border-emerald-200",
  slate: "bg-slate-50 text-slate-700 border-slate-200",
};
function Kpi({ label, value, icon: Icon, tone, testid }) {
  return (
    <div className={`p-4 rounded-lg border ${TONES[tone] || TONES.slate}`} data-testid={testid}>
      <div className="flex items-center justify-between mb-2">
        <div className="text-xs font-semibold uppercase tracking-wider">{label}</div>
        <Icon className="w-4 h-4" />
      </div>
      <div className="text-2xl font-bold">{value}</div>
    </div>
  );
}
